'use client';

// The first thing anyone sees at app.crie3.com.br.
//
// It replaced the login form as the entry point, so it carries a load the login
// form never did: a visitor who has never heard of Crie3 has to understand what
// it is, what it costs, and how to start — before there is an account to
// authenticate. `Entrar` stays one click away in the header for the people who
// already have one.
//
// Layout is the app's own, not a marketing theme: the tokens in
// `styles/tokens.css` (warm neutral surfaces, the terracotta accent) and the
// easing rule from AGENTS.md. Someone arriving from this page into the product
// should not feel they changed products.
//
// Copy is pt-BR literals, matching `LoginView`'s documented choice — this whole
// pre-auth surface is the hosted-Brazil product and does not go through the
// i18n Dict.
//
// Prices come from `plans.ts` and may legitimately be absent; see the note
// there. When they are, the plan buttons become a conversation instead of a
// checkout, so shipping before the table is set degrades into an honest page
// rather than one showing a number nobody chose.

import { Button } from '@open-design/components';

import { LegalNotice } from './LegalNotice';
import {
  planCatalogueWithPricing,
  PLAN_BASELINE,
  formatPriceBRL,
  formatTokenAllowance,
  hasPublishedPricing,
  type Crie3Plan,
} from './plans';
import { usePlanPricing } from './usePlanPricing';
import styles from './SalesPage.module.css';
import { Crie3Wordmark } from '../components/Crie3Wordmark';

export interface SalesPageProps {
  /** Open the login form, already on the sign-in tab. */
  onSignIn: () => void;
  /** Open the login form, already on the create-account tab. */
  onSignUp: () => void;
}

/** What the product does, in the words of someone who has to deliver on Sunday. */
const CAPABILITIES = [
  {
    title: 'Descreva a peça. Ela sai pronta.',
    body:
      'Conte o tema do culto, a data e o versículo. O agente escreve o texto, '
      + 'compõe a arte e entrega o post, o carrossel ou o story — na identidade '
      + 'visual que você definiu, não num template genérico.',
  },
  {
    title: 'A marca é do cliente, não do software.',
    body:
      'Envie o logo, as fotos e o material de marca. O sistema aprende a paleta, '
      + 'a tipografia e o jeito de falar, e cada cliente que você atende fica '
      + 'separado do outro.',
  },
  {
    title: 'Do arquivo ao feed, sem sair daqui.',
    body:
      'Conecte Instagram, Facebook ou TikTok e aprove a publicação com um gesto '
      + 'seu. Nada vai ao ar sozinho — a aprovação é sempre de uma pessoa.',
  },
  {
    title: 'Ele lembra da igreja.',
    body:
      'Nome do pastor, nome dos cultos, o que já foi publicado, o que não pode '
      + 'ser dito. Você não recomeça a explicação a cada conversa.',
  },
];

const FAQ = [
  {
    q: 'Preciso saber desenhar?',
    a:
      'Não. Você precisa saber o que quer comunicar. A parte visual é o que o '
      + 'Crie3 faz — e você ajusta o que não gostou conversando, do mesmo jeito.',
  },
  {
    q: 'A arte sai com a cara da minha igreja ou com cara de robô?',
    a:
      'Com a cara da sua igreja, se você der o material dela. Sem logo, sem '
      + 'fotos e sem paleta, qualquer ferramenta entrega algo genérico — aqui '
      + 'inclusive.',
  },
  {
    q: 'Posso usar comercialmente o que for gerado?',
    a:
      'Pode. E vale ler os Termos de Uso: não garantimos exclusividade, e a '
      + 'revisão do que vai ao ar é sempre sua.',
  },
  {
    q: 'O que acontece se eu acabar os tokens do mês?',
    a:
      'A criação pausa até a virada do ciclo ou até você subir de plano. Nada é '
      + 'apagado, e o que você já produziu continua seu.',
  },
  {
    q: 'Meus dados e os da minha igreja ficam guardados como?',
    a:
      'Cada assinante tem seu próprio espaço, separado dos demais. O que você '
      + 'envia não é usado para treinar modelo nenhum. A Política de '
      + 'Privacidade abaixo detalha, inclusive sobre dado sensível.',
  },
];

function PlanCard({ plan, onChoose }: { plan: Crie3Plan; onChoose: () => void }) {
  const priced = plan.priceMonthlyBRL !== null;
  return (
    <article className={`${styles.plan}${plan.featured ? ` ${styles.planFeatured}` : ''}`}>
      {plan.featured ? <span className={styles.planTag}>Mais escolhido</span> : null}
      <h3 className={styles.planName}>{plan.name}</h3>
      <p className={styles.planTagline}>{plan.tagline}</p>

      <p className={styles.planPrice}>
        {priced ? (
          <>
            <strong>{formatPriceBRL(plan.priceMonthlyBRL as number)}</strong>
            <span className={styles.planPer}>/mês</span>
          </>
        ) : (
          <span className={styles.planPricePending}>Preço a definir</span>
        )}
      </p>

      <ul className={styles.planList}>
        <li>
          <strong>{formatTokenAllowance(plan.tokenCap)}</strong> por mês
        </li>
        {/* Only a POSITIVE allowance earns a bullet. Zero is a real stored
            value — the owner setting Starter to "no client brands" — but
            rendered here it reads as a pricing card advertising "0 marcas de
            cliente", which sells against itself. What the tier does include is
            already said by its own highlight ("Sua própria marca, sempre
            inclusa"). Caught by looking at the page, not by a test: every
            assertion about this list still passed. */}
        {plan.clientBrands !== null && plan.clientBrands > 0 ? (
          <li>
            <strong>{plan.clientBrands}</strong>{' '}
            {plan.clientBrands === 1 ? 'marca de cliente' : 'marcas de cliente'}
          </li>
        ) : null}
        {plan.highlights.map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>

      <Button
        variant={plan.featured ? 'primary' : 'default'}
        className={styles.planCta}
        onClick={onChoose}
      >
        {priced ? 'Assinar' : 'Falar com a gente'}
      </Button>
    </article>
  );
}

export function SalesPage({ onSignIn, onSignUp }: SalesPageProps) {
  // The prices come from the daemon, not from this bundle: the owner sets them
  // in Settings → Planos and this page changes without a rebuild. Until the
  // fetch lands the catalogue reads "Preço a definir", which is also what an
  // unreachable daemon and an unset table look like — three ways of not
  // knowing a price, all rendered as not knowing it.
  const { pricing } = usePlanPricing();
  const plans = planCatalogueWithPricing(pricing);
  const priced = hasPublishedPricing(plans);

  return (
    <div className={styles.root}>
      <header className={styles.header}>
        <div className={styles.brand}>
                    <Crie3Wordmark className={styles.brandName} height="32px" />
        </div>
        <nav className={styles.nav}>
          <a className={styles.navLink} href="#planos">
            Planos
          </a>
          <Button variant="ghost" onClick={onSignIn}>
            Entrar
          </Button>
        </nav>
      </header>

      <main>
        <section className={styles.hero}>
          <p className={styles.kicker}>Para quem comunica igreja</p>
          <h1 className={styles.headline}>
            A arte do culto de domingo, pronta numa conversa de sábado.
          </h1>
          <p className={styles.sub}>
            Crie3 é o estúdio de quem produz para igrejas e ministérios: posts,
            carrosséis e stories na identidade visual da marca, sem abrir um
            editor de imagem.
          </p>
          <div className={styles.heroCtas}>
            <Button variant="primary" className={styles.heroPrimary} onClick={onSignUp}>
              Criar conta
            </Button>
            <Button variant="ghost" onClick={onSignIn}>
              Já tenho conta
            </Button>
          </div>
        </section>

        <section className={styles.capabilities} aria-label="O que o Crie3 faz">
          {CAPABILITIES.map((item) => (
            <article key={item.title} className={styles.capability}>
              <h2>{item.title}</h2>
              <p>{item.body}</p>
            </article>
          ))}
        </section>

        <section className={styles.plans} id="planos" aria-label="Planos">
          <h2 className={styles.sectionTitle}>Planos</h2>
          <p className={styles.sectionSub}>
            Todos os planos incluem o produto inteiro. O que muda é quanto você
            produz por mês e quantas marcas de cliente você atende.
          </p>

          <div className={styles.planGrid}>
            {plans.map((plan) => (
              <PlanCard key={plan.id} plan={plan} onChoose={onSignUp} />
            ))}
          </div>

          {!priced ? (
            <p className={styles.pricingNote}>
              A tabela de preços está sendo fechada. Crie sua conta e a gente
              conversa sobre o plano certo para o seu volume.
            </p>
          ) : null}

          <div className={styles.baseline}>
            <h3>Em todos os planos</h3>
            <ul>
              {PLAN_BASELINE.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          </div>
        </section>

        <section className={styles.faq} aria-label="Perguntas frequentes">
          <h2 className={styles.sectionTitle}>Perguntas frequentes</h2>
          <div className={styles.faqList}>
            {FAQ.map((item) => (
              <details key={item.q} className={styles.faqItem}>
                <summary>{item.q}</summary>
                <p>{item.a}</p>
              </details>
            ))}
          </div>
        </section>

        <section className={styles.closing}>
          <h2>Comece pela próxima peça que você precisa entregar.</h2>
          <Button variant="primary" className={styles.heroPrimary} onClick={onSignUp}>
            Criar conta
          </Button>
        </section>
      </main>

      <footer className={styles.footer}>
        <LegalNotice />
        <p className={styles.footerMark}><Crie3Wordmark height="24px" /></p>
      </footer>
    </div>
  );
}
